import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";

import bg1 from '../../../assets/images/bg/blog01.jpg';
import NavbarTwo from "../../../components/navbar/navbarTwo";
import ScrollTop from "../../../components/scrollTop";
import FooterThree from "../../../components/footer/footerThree";
import AdminCreateInsuranceBlogPost from "../../../components/blog/adminCreateInsuranceBlogPost";
import { FiArrowRight } from "../../../assets/icons/vander";

// Import Firebase dependencies
import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, collection, getDocs } from 'firebase/firestore';

import app from "../../../firebase/firebase";

// Initialize Auth, and Firestore
const auth = getAuth(app);
const db = getFirestore(app);

export default function BlogList() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  // Listen for auth state changes and check for admin custom claims.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        user.getIdTokenResult()
          .then((idTokenResult) => {
            // Check if the admin claim is present.
            if (idTokenResult.claims.admin) {
              setIsAuthenticated(true);
            } else {
              setIsAuthenticated(false);
            }
            setCurrentUserEmail(user.email);
          })
          .catch((error) => {
            console.error("Error retrieving token claims:", error);
            setIsAuthenticated(false);
            setCurrentUserEmail(user.email);
          });
      } else {
        setIsAuthenticated(false);
        setCurrentUserEmail(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch posts from Firestore's "insurance-blog" collection.
  useEffect(() => {
    async function fetchPosts() {
      setLoadingPosts(true);
      try {
        const postsCollection = collection(db, "insurance-blog");
        const querySnapshot = await getDocs(postsCollection);
        const postsData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title,
            image: data["header-image-url"],
            desc: data.description,
            tag: data.tags && data.tags.length > 0 ? data.tags[0] : ""
          };
        });
        setPosts(postsData);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoadingPosts(false);
      }
    }
    fetchPosts();
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      user.getIdTokenResult().then((idTokenResult) => {
        if (idTokenResult.claims.admin) {
          setIsAuthenticated(true);
          setCurrentUserEmail(user.email);
        } else {
          alert("You do not have admin privileges.");
          signOut(auth);
        }
      });
    } catch (error) {
      console.error("Error signing in", error);
      alert("Error signing in");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setIsAuthenticated(false);
      setCurrentUserEmail(null);
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  return (
    <>
      <NavbarTwo navClass="defaultscroll sticky" manuClass="navigation-menu nav-light nav-right" />
      <section id="page-top" className="bg-half-170 d-table w-100" style={{ backgroundImage: `url(${bg1})` }}>
        <div className="bg-overlay bg-gradient-overlay"></div>
        <div className="container">
          <div className="row mt-5 justify-content-center">
            <div className="col-12">
              <div className="title-heading text-center">
                <h5 className="heading fw-semibold page-heading text-white title-dark">Blog & News</h5>
                <p className="text-white-50 para-desc mx-auto mb-0">Our Latest Blog and News in Grid Layouts</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <AdminCreateInsuranceBlogPost isAuthenticated={isAuthenticated} />
          <div className="row">
            {loadingPosts ? (
              Array.from(new Array(4)).map((_, index) => (
                <div className="col-lg-6 mb-4 pb-2" key={index}>
                  <div className="card blog blog-primary shadow rounded overflow-hidden">
                    <div className="row align-items-center g-0">
                      <div className="col-md-6">
                        <Skeleton variant="rectangular" width="100%" height={150} />
                      </div>
                      <div className="col-md-6">
                        <div className="card-body content">
                          <Skeleton variant="text" width="80%" height={30} />
                          <Skeleton variant="text" width="100%" />
                          <Skeleton variant="text" width="60%" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              posts.map((item, index) => (
                <div className="col-lg-6 mb-4 pb-2" key={index}>
                  <div className="card blog blog-primary shadow rounded overflow-hidden">
                    <div className="row align-items-center g-0">
                      <div className="col-md-6">
                        <div className="image card-img position-relative overflow-hidden">
                          <img src={item.image} className="img-fluid" alt="" />
                          <div className="card-overlay"></div>
                          {item.tag && (
                            <div className="blog-tag">
                              <Link to="#" className="badge text-bg-light">
                                {item.tag}
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="card-body content">
                          <Link
                            to={`/insurance-blog/${item.id}`}
                            className="h5 title text-dark d-block mb-0"
                          >
                            {item.title}
                          </Link>
                          <p className="text-muted mt-2 mb-2">{item.desc}</p>
                          <Link to={`/insurance-blog/${item.id}`} className="link text-dark">
                            Read More <FiArrowRight className="align-middle" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="row mt-4">
            <div className="col-12">
              <ul className="pagination justify-content-center mb-0">
                <li className="page-item">
                  <Link className="page-link" to="#" aria-label="Previous">
                    <span aria-hidden="true"><i className="mdi mdi-chevron-left mdi-18px"></i></span>
                  </Link>
                </li>
                <li className="page-item"><Link className="page-link active" to="#">1</Link></li>
                <li className="page-item">
                  <Link className="page-link" to="#" aria-label="Next">
                    <span aria-hidden="true"><i className="mdi mdi-chevron-right mdi-18px"></i></span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Google Sign In / Sign Out Section */}
      <div className="text-center my-4">
        {isAuthenticated ? (
          <div>
            <p>Signed in as {currentUserEmail}. You can now create a new blog post!</p>
            <button onClick={handleSignOut} className="btn btn-danger">
              Sign Out
            </button>
          </div>
        ) : (
          <button onClick={handleGoogleSignIn} className="btn btn-secondary">
            Admin Sign In
          </button>
        )}
      </div>

      <FooterThree />
      <ScrollTop />
    </>
  );
}
